import { useNavigation } from '@/app/hooks'
import { useAppSelector } from '@/hooks'
import { thermostatsCollection } from '@/reducers/thermostats'
import { vehicleChargersCollection } from '@/reducers/vehicleChargers'
import { useLocation, useParams } from 'react-router-dom'

export function useConfigureDeviceFlow() {
  const vehicles = useAppSelector((state) => state.vehicles.vehicles)
  const vehiclesLoading = useAppSelector((state) => state.vehicles.isLoading)
  const { data: thermostats, isLoadingOrIdle: thermostatsLoading } =
    thermostatsCollection.useFetch()
  const { data: chargers, isLoadingOrIdle: chargersLoading } =
    vehicleChargersCollection.useFetch()
  const navigate = useNavigation()
  const location = useLocation()
  const { deviceId } = useParams<{ deviceId: string }>()
  const deviceIdsSearchParam = new URLSearchParams(location.search).get(
    'deviceIds',
  )
  const deviceIds = deviceIdsSearchParam ? deviceIdsSearchParam.split(',') : []
  const isLoading = vehiclesLoading || thermostatsLoading || chargersLoading
  const getSteps = () => {
    if (isLoading) {
      return null
    }
    const vehicleSteps = (vehicles ?? []).map((vehicle) => ({
      deviceId: vehicle.id,
      deviceType: ConfigureDeviceType.Vehicle,
      url: `/configure-devices/${vehicle.id}/vehicle`,
    }))
    const thermostatSteps = thermostats.map((thermostat) => ({
      deviceId: thermostat.id,
      deviceType: ConfigureDeviceType.Thermostat,
      url: `/configure-devices/${thermostat.id}/thermostat`,
    }))
    const chargerSteps = chargers.map((charger) => ({
      deviceId: charger.id,
      deviceType: ConfigureDeviceType.Charger,
      url: `/configure-devices/${charger.id}/charger`,
    }))
    const mobileStep = {
      deviceId: 'mobile',
      deviceType: ConfigureDeviceType.Mobile,
      url: `/configure-devices/mobile/push-notifications`,
    }

    const steps = [
      ...vehicleSteps,
      ...thermostatSteps,
      ...chargerSteps,
      mobileStep,
    ].filter((d) => deviceIds.includes(d.deviceId.toString()))

    return steps
  }

  const steps = getSteps()
  const totalSteps = steps?.length
  const currentStepIndex = steps?.findIndex(
    (s) => s.deviceId.toString() === deviceId,
  )
  const completedSteps = currentStepIndex === -1 ? undefined : currentStepIndex
  const progress =
    completedSteps !== undefined && totalSteps !== undefined
      ? { totalSteps, completedSteps }
      : null

  const nextStep = () => {
    if (currentStepIndex === undefined) {
      return
    }
    const nextStep = steps?.[currentStepIndex + 1]
    if (nextStep) {
      navigate.pushRetainingParams(nextStep.url)
      return
    }
    navigate.push('/devices')
  }

  const initFlow = ({
    deviceIds,
    setupMobile,
  }: {
    deviceIds: (number | string)[]
    setupMobile: boolean
  }) => {
    if (setupMobile) {
      deviceIds.push('mobile')
    }
    const searchParams = new URLSearchParams([['deviceIds', deviceIds.join()]])
    navigate.push(`/configure-devices/init?${searchParams.toString()}`)
  }

  return { deviceIds, steps, progress, nextStep, initFlow }
}

export enum ConfigureDeviceType {
  Vehicle = 'vehicle',
  Thermostat = 'thermostat',
  Charger = 'charger',
  Mobile = 'mobile',
}
